import { useCallback, useEffect, useMemo, useState } from 'react'

import usePrevious from '@common/hooks/usePrevious'
import useSelect from '@common/hooks/useSelect'

import { DEFAULT_SELECT_SIZE, SELECT_SIZE_TO_HEIGHT } from './styles'
import { SectionedSelectProps, SelectProps, SelectValue } from './types'
import useSelectKeyboardControl from './useSelectKeyboardControl'

type Props = Pick<
  SelectProps,
  'menuOptionHeight' | 'setValue' | 'size' | 'attemptToFetchMoreOptions' | 'mode' | 'menuPosition'
> & {
  value: SelectProps['value']
  data: SectionedSelectProps['sections']
  stickySectionHeadersEnabled?: boolean
  headerHeight?: number
  onSearch?: (searchTerm: string) => void
  withNameFilterChips?: boolean
}

const NO_CHIPS: string[] = []

// LibertySwap parity: a token-name chip (e.g. "USDC") must surface ONLY that
// exact symbol across every chain — never USDC.e / USDbC / name-substring hits.
// A few symbols share a wrapped/native pair and are treated as one group.
const SYMBOL_ALIAS_GROUPS: Record<string, string[]> = {
  eth: ['eth', 'weth'],
  weth: ['eth', 'weth'],
  pls: ['pls', 'wpls'],
  wpls: ['pls', 'wpls']
}

const getExactSymbolGroup = (symbol: string): string[] =>
  SYMBOL_ALIAS_GROUPS[symbol] || [symbol]

const useSelectInternal = ({
  menuOptionHeight,
  setValue,
  value,
  size = DEFAULT_SELECT_SIZE,
  data,
  stickySectionHeadersEnabled,
  headerHeight = 0,
  attemptToFetchMoreOptions,
  mode = 'select',
  menuPosition,
  onSearch,
  withNameFilterChips
}: Props) => {
  const useSelectReturnValue = useSelect({ menuPosition })
  const { search, isMenuOpen, setIsMenuOpen, setSearch } = useSelectReturnValue
  const optionHeight = menuOptionHeight || SELECT_SIZE_TO_HEIGHT[size]

  const [listHeight, setListHeight] = useState(0)

  const handleOptionSelect = useCallback(
    (item: SelectValue) => {
      !!setValue && setValue(item)
      // Close only — do NOT clear the search term here. Clearing it synchronously
      // repaints the list to the full, unfiltered set for the duration of the
      // close animation (a visible "flash"/reset right before the popup
      // disappears), which is especially obvious now the picker opens pre-filtered
      // to a symbol. The on-close effect below clears the term once the menu is
      // actually closed, so the list stays consistent while it animates out.
      setIsMenuOpen(false)
    },
    [setValue, setIsMenuOpen]
  )

  const prevSearch = usePrevious(search)
  const prevIsMenuOpen = usePrevious(isMenuOpen)

  // Unique token symbols across every option (all chains), preserving first-seen
  // order. Derived from the full unfiltered `data` so the chips stay constant
  // while a chip filter is active.
  const nameFilterChips = useMemo(() => {
    if (!withNameFilterChips) return NO_CHIPS

    // First collect every symbol present so we can tell whether a group's
    // canonical (native) member exists before dropping its wrapped alias.
    const presentKeys = new Set<string>()
    data.forEach((section) => {
      ;(section.data as SelectProps['options']).forEach((option) => {
        const symbol = (option.symbol || option.extraSearchProps?.symbol)?.trim()
        if (symbol) presentKeys.add(symbol.toLowerCase())
      })
    })

    const seen = new Set<string>()
    const chips: string[] = []
    data.forEach((section) => {
      ;(section.data as SelectProps['options']).forEach((option) => {
        const symbol = (option.symbol || option.extraSearchProps?.symbol)?.trim()
        if (!symbol) return
        const key = symbol.toLowerCase()
        // Collapse wrapped/native alias groups (e.g. PLS/WPLS, ETH/WETH) into a
        // single chip: the native member's chip already surfaces the whole group
        // (getExactSymbolGroup), so a separate WPLS/WETH chip is redundant. Drop
        // the non-canonical alias whenever its canonical member is also listed.
        const group = SYMBOL_ALIAS_GROUPS[key]
        if (group && key !== group[0] && presentKeys.has(group[0]!)) return
        if (seen.has(key)) return
        seen.add(key)
        chips.push(symbol)
      })
    })
    return chips
  }, [withNameFilterChips, data])

  const filteredData = useMemo(() => {
    const normalizedSearchTerm = search.trim().toLowerCase()

    const hasNewSearchTerm = onSearch && search !== prevSearch
    if (hasNewSearchTerm) onSearch(search)

    if (!search) return data

    // LibertySwap-style chip filter: when the search term exactly matches one of
    // the token-name chips (a chip tap, or a fully typed symbol), show ONLY that
    // symbol's tokens across every chain — exact symbol match, no partial/name
    // matches. This is what keeps the list from looking "messy" (USDC.e, USDbC,
    // name-substring hits) when a chip is active.
    const isChipTerm = withNameFilterChips && nameFilterChips.some((c) => c.toLowerCase() === normalizedSearchTerm)
    if (isChipTerm) {
      const symbolGroup = getExactSymbolGroup(normalizedSearchTerm)
      const sectionsBySymbol = data.map((section) => ({
        ...section,
        data: (section.data as SelectProps['options']).filter((o) => {
          const sym = (o.symbol || o.extraSearchProps?.symbol || '').toString().toLowerCase()
          return symbolGroup.includes(sym)
        })
      }))
      const noMatches = sectionsBySymbol.every((section) => section.data.length === 0)
      return noMatches ? [] : sectionsBySymbol
    }

    const filterOptions = (options: SelectProps['options']) => {
      // Search term split by spaces to partially match separated terms.
      const searchWords = normalizedSearchTerm.split(/\s+/)

      const { exactMatches, partialMatches } = options.reduce(
        (result, o) => {
          const { value: optionValue, label, extraSearchProps } = o

          const fieldsToBeSearchedInto = [
            optionValue.toString().toLowerCase(),
            // In case the label is string, include it (could be any ReactNode)
            typeof label === 'string' ? label.toLowerCase() : '',
            ...(extraSearchProps
              ? Object.values(extraSearchProps).map((field: unknown) => String(field).toLowerCase())
              : [])
          ]

          // Exact match - if any field fully equals the full search term
          const isExactMatch = fieldsToBeSearchedInto.some((f) => f === normalizedSearchTerm)

          // Partial match - if all search words are found in any field
          // Example: If we search for 'WALLET Ethereum',
          // both search words should be found in any of the fields being searched.
          const isPartialMatch = searchWords.every((word) =>
            fieldsToBeSearchedInto.some((f) => f.includes(word))
          )

          // Prioritize exact matches, partial matches come after
          if (isExactMatch) {
            result.exactMatches.push(o)
          } else if (isPartialMatch) {
            result.partialMatches.push(o)
          }

          return result
        },
        { exactMatches: [] as SelectValue[], partialMatches: [] as SelectValue[] }
      )

      return { exactMatches, partialMatches }
    }

    const sectionsWithFilteredData = data.map((section) => {
      const { exactMatches, partialMatches } = filterOptions(section.data as SelectProps['options'])

      return {
        ...section,
        data: [...exactMatches, ...partialMatches]
      }
    })
    const noMatchesFound = sectionsWithFilteredData.every((section) => section.data.length === 0)
    const isAnotherSearchTerm = search !== prevSearch
    const shouldAttemptToFetchMoreOptions =
      noMatchesFound && isAnotherSearchTerm && !!attemptToFetchMoreOptions
    if (shouldAttemptToFetchMoreOptions) attemptToFetchMoreOptions(search)

    return noMatchesFound ? [] : sectionsWithFilteredData
  }, [search, onSearch, prevSearch, data, attemptToFetchMoreOptions, withNameFilterChips, nameFilterChips])

  const keyExtractor = useCallback((item: SelectValue) => item.key || item.value, [])

  const getItemLayout = useCallback(
    (d: SelectValue[] | null | undefined, index: number) => {
      return {
        length: optionHeight,
        offset: optionHeight * index,
        index
      }
    },
    [optionHeight]
  )

  const handleLayout = useCallback((event: any) => {
    const { height } = event.nativeEvent.layout
    setListHeight(height)
  }, [])

  // Normalize the search term when the menu OPENS — never on close. Clearing it
  // on close repaints the still-visible list to the full, unfiltered set for the
  // duration of the close animation (a "flash"/reset right before the popup
  // disappears). Resetting at open instead keeps the list consistent while the
  // popup animates out, and still gives every open a clean starting state.
  //
  // LibertySwap parity: when the picker has name-filter chips, opening
  // pre-activates the chip of the currently selected token, so the list lands
  // pre-filtered to that symbol (selected token in view) instead of a mixed,
  // "messy" list. When nothing is selected yet (e.g. first-ever open before
  // any token has been picked), default to the USDC chip instead of leaving
  // every chip inactive. Other selects just open with an empty search.
  useEffect(() => {
    if (prevIsMenuOpen || !isMenuOpen) return
    if (!withNameFilterChips) {
      setSearch('search', '')
      return
    }
    const selectedSymbol = (value as SelectValue | null)?.symbol
    setSearch('search', selectedSymbol ? String(selectedSymbol) : 'USDC')
  }, [isMenuOpen, prevIsMenuOpen, withNameFilterChips, value, setSearch])

  const { listRef, renderItem, handleScroll } = useSelectKeyboardControl({
    listHeight,
    optionHeight,
    headerHeight,
    sections: filteredData,
    value,
    size,
    isMenuOpen,
    stickySectionHeadersEnabled,
    setIsMenuOpen,
    handleOptionSelect,
    mode
  })

  return {
    ...useSelectReturnValue,
    nameFilterChips,
    filteredData,
    listRef,
    renderItem,
    keyExtractor,
    getItemLayout,
    handleScroll,
    handleLayout
  }
}

export default useSelectInternal
